import { FormConfig, FormField } from 'components/form/FormConfig';
import {
  ALL_VENDOR_OPTION,
  ECSegmentFormData,
  ECSegmentType,
  EventType
} from 'core/ecSegment/ECSegment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectOptions } from 'components/commonType';
import {
  DefaultProductFilterManager,
  ProductFilterManager
} from 'core/product/ProductFilterManager';
import i18n from 'i18n';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCoreContext } from 'contexts/coreContext';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { InventorySetting } from 'containers/Limitations/Inventory';
import { useFormikContext } from 'formik';
import { camelCase, isEmpty, isEqual, set, trim } from 'lodash';
import { renderFormikProductCategoriesSelectField } from '../components/ProductCategorySelectField';
import { collapseSelectedPaths } from '../components/ProductCategoryPathUtils';
import { DEFAULT_INVENTORY } from 'containers/Limitations/LimitationSetting/LimitationSettingData';
import { CategoryData } from 'core/product/Product';
import styles from '../components/productCategorySelectField.module.scss';
import { renderFormikVendorNumberSelectField } from '../components/VendorNumberSelectField';
import { generateI18nOfSelectOptions } from 'utils/I18nUtils';

const defaultInventorySetting: InventorySetting = {
  name: DEFAULT_INVENTORY,
  cb: () => Promise.resolve([])
};
const productFilterManager: ProductFilterManager =
  new DefaultProductFilterManager();
const eventTypeOptions: SelectOptions[] = createSelectOptionsFromEnum(
  EventType,
  'ecSegment.eventTypes.',
  [EventType.SEARCH],
  camelCase
);

const createCategoryOptions = (
  categoryData: CategoryData,
  parentPath: string
) => {
  const options: SelectOptions[] = [];
  Object.keys(categoryData).forEach((key) => {
    const value = categoryData[key];
    const path = `${parentPath} > ${key}`;
    if (Object.keys(value).length === 0) {
      const newOption = { label: i18n.t<string>(`productCategories.${key}`), value: path };
      options.push(newOption);
    } else {
      const newOption = {
        label: i18n.t<string>(`productCategories.${key}`),
        value: path,
        options: createCategoryOptions(value, path)
      };
      options.push(newOption);
    }
  });
  return options;
};

export const useProductCategorySegmentFields = (
  type: ECSegmentType
): {
  loading: boolean;
  fields: FormField[];
} => {
  const { loading, callAPIs } = useCallAPI();

  const [vendorNumberOptions, setVendorNumberOptions] = useState<SelectOptions[]>([]);
  const [categoryInventorySettings, setCategoryInventorySettings] = useState<InventorySetting[]>([defaultInventorySetting]);
  const { values, setFieldValue } = useFormikContext<ECSegmentFormData>();
  const vendorNumber = values.vendorNumber;

  const inclusionRef = useRef<string[]>(values.inclusion);
  useEffect(() => {
    inclusionRef.current = values.inclusion;
  }, [values.inclusion]);

  const core = useCoreContext();
  const localeMeta = core?.accountManager.localeMeta;
  const isAgency = localeMeta?.isAgency;
  const agencyVendorNumber = localeMeta?.vendorNumber;
  const agencyId = localeMeta?.agencyId;

  useEffect(() => {
    if (type !== ECSegmentType.PRODUCT_CATEGORY) {
      return;
    }

    if (!isAgency) {
      const defalutVendorNumber = agencyVendorNumber || ALL_VENDOR_OPTION.value.toString();
      setVendorNumberOptions([
        { label: defalutVendorNumber, value: defalutVendorNumber }
      ]);
      return;
    }

    callAPIs(
      [() => productFilterManager.getVendorNumberOptions(agencyId)],
      setVendorNumberOptions
    );
  }, [type, isAgency, agencyId, agencyVendorNumber, callAPIs]);

  const renderCategoriesSelectField = useCallback(() => {
    return renderFormikProductCategoriesSelectField(
      'inclusion',
      categoryInventorySettings
    );
  }, [categoryInventorySettings]);

  useEffect(() => {
    if (type !== ECSegmentType.PRODUCT_CATEGORY) {
      setCategoryInventorySettings([defaultInventorySetting]);
      return;
    }

    if (!vendorNumber) {
      setCategoryInventorySettings([defaultInventorySetting]);
      return;
    }

    callAPIs(
      [
        () =>
          vendorNumber === ALL_VENDOR_OPTION.value.toString()
            ? productFilterManager.getAllCategories()
            : productFilterManager.getCategories(vendorNumber)
      ],
      (categoriesOfVendor: SelectOptions[]) => {
        if (isEmpty(categoriesOfVendor)) {
          setCategoryInventorySettings([defaultInventorySetting]);
          return;
        }
        const categoriesData: CategoryData = {};
        categoriesOfVendor.forEach((option) => {
          generateI18nOfSelectOptions(option, 'productCategories');
          const names = option.label.split('>').map(trim);
          const path = option.value.toString();
          const keys = path.split('>').map(trim);
          for (let i = 0; i < keys.length; i++) {
            generateI18nOfSelectOptions({
              label: names[i],
              value: keys[i]
            }, 'productCategories');
          }
          const resultPath = keys
            .reduce((acc, key) => `${acc}.${key}`, '')
            .slice(1);
          set(categoriesData, resultPath, {});
        });
        const inventorySettings = Object.keys(categoriesData).map(
          (mainCategory) => {
            const categoryOptions = createCategoryOptions(
              categoriesData[mainCategory],
              mainCategory
            );
            return {
              name: mainCategory,
              title: i18n.t<string>(`productCategories.${mainCategory}`),
              cb: () => categoryOptions
            };
          }
        );
        setCategoryInventorySettings([
          defaultInventorySetting,
          ...inventorySettings
        ]);

        // 編輯既有受眾包時，已存的是攤平後的下層清單。分類樹到手之後才有辦法判斷
        // 「某個父層底下是否全選」，所以反推只在這裡做一次，不隨後續的選取變動重跑
        // （否則使用者手動點滿子項時會被意外收合）。
        const collapsedInclusion = collapseSelectedPaths(
          inclusionRef.current,
          inventorySettings.map((setting) => setting.cb())
        );
        if (!isEqual(collapsedInclusion, inclusionRef.current)) {
          setFieldValue('inclusion', collapsedInclusion);
        }
      }
    );
  }, [vendorNumber, type, callAPIs, setFieldValue]);

  const onVendorNumberChange = useCallback(() => {
    setFieldValue('inclusion', []);
  }, [setFieldValue]);

  const renderVendorNumberSelectField = useCallback(() => {
    return renderFormikVendorNumberSelectField(
      'vendorNumber',
      vendorNumberOptions,
      onVendorNumberChange
    );
  }, [vendorNumberOptions, onVendorNumberChange]);

  const fields = useMemo(() => {
    if (type !== ECSegmentType.PRODUCT_CATEGORY) {
      return [];
    }
    return new FormConfig.FieldsBuilder()
      .addFormikCustom({
        label: i18n.t<string>('ecSegmentForm.labels.vendor'),
        name: 'vendorNumberContainer',
        render: renderVendorNumberSelectField
      })
      .addFormikSelect({
        label: i18n.t<string>('ecSegmentForm.labels.eventType'),
        name: 'eventType',
        options: eventTypeOptions,
        simpleValue: true
      })
      .addFormikCustom({
        label: i18n.t<string>('ecSegmentForm.labels.inclusion'),
        name: 'inclusionContainer',
        render: renderCategoriesSelectField,
        fieldContentClassName: styles.productCategoriesField
      })
      .build();
  }, [type, renderVendorNumberSelectField, renderCategoriesSelectField]);

  return {
    loading,
    fields
  };
};
